/**
 * DistrictShare — share button for a single postcode district.
 *
 * Two variants:
 *   - DistrictShare (default export): popover button, designed to sit
 *     absolutely-positioned inside a parent <Link> wrapper. stopBubble.
 *   - DistrictShareInline (named export): horizontal button row, for
 *     contexts that aren't wrapped in a Link (e.g. SearchPage banner).
 *
 * Both use useCardShare hook → native browser download (no fetch/blob),
 * LinkedIn-pending state for the global ShareConfirmListener toast.
 */
import { useState, useRef, useEffect } from 'react';
import { Share2, Download, Linkedin, Link2, Check, X } from 'lucide-react';
import useCardShare from '../hooks/useCardShare';

function _buildShare({ district, name, invisiblePct }) {
  const shareUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/search?district=${district}`
    : '';
  const captionPctTxt = invisiblePct != null ? `${Math.round(invisiblePct)}% digitally invisible · ` : '';
  const caption = `${district} (${name || 'Tees Valley'}) — ${captionPctTxt}coverage is not connectivity. Adoptic · Tees Valley Digital Register · Source: Companies House + Ofcom Jan 2026.`;
  return { shareUrl, caption };
}

export default function DistrictShare({ district, name, invisiblePct }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  const { shareUrl, caption } = _buildShare({ district, name, invisiblePct });
  const { liUrl, downloadPng, openLinkedIn, copyLink } = useCardShare({
    target: district,
    cardPath: `postcode-card/${district}.png`,
    shareUrl,
    caption,
    filename: `stockton-${district.toLowerCase()}-reality.png`,
  });

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const stop = (e) => { e.stopPropagation(); e.preventDefault(); };

  const handleDownload = (e) => {
    stop(e);
    downloadPng();
    setOpen(false);
  };

  const handleLinkedIn = (e) => {
    stop(e);
    openLinkedIn();
    setOpen(false);
  };

  const handleCopy = async (e) => {
    stop(e);
    const ok = await copyLink();
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  return (
    <div ref={ref} className="absolute top-2 right-2 z-10" onClick={stop}>
      <button
        onClick={(e) => { stop(e); setOpen(v => !v); }}
        title={`Share ${district} reality card`}
        aria-label={`Share ${district} reality card`}
        className="w-7 h-7 flex items-center justify-center rounded-full bg-ink/[0.06] hover:bg-brand-orange/15 hover:text-brand-orange text-ink/55 transition-colors"
        data-testid={`district-share-btn-${district}`}
      >
        <Share2 className="w-3.5 h-3.5" strokeWidth={2} />
      </button>

      {open && (
        <div
          onClick={stop}
          className="absolute right-0 mt-1.5 w-[228px] rounded-[6px] bg-white border border-ink/12 shadow-[0_8px_24px_-4px_rgba(0,0,0,0.18)] p-2 animate-fade-in"
          data-testid={`district-share-menu-${district}`}
        >
          <div className="flex items-center justify-between px-1.5 pt-0.5 pb-1.5 border-b border-ink/8 mb-1">
            <span className="font-display text-[11px] font-extrabold text-ink uppercase tracking-wider">Share {district}</span>
            <button onClick={(e) => { stop(e); setOpen(false); }} className="text-ink/40 hover:text-ink">
              <X className="w-3 h-3" />
            </button>
          </div>
          <button
            onClick={handleDownload}
            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-[4px] hover:bg-brand-orange/8 text-left transition-colors"
            data-testid={`district-share-download-${district}`}
          >
            <Download className="w-3.5 h-3.5 text-brand-orange" strokeWidth={1.8} />
            <div className="flex-1 min-w-0">
              <div className="font-body text-[11.5px] font-medium text-ink">Download card</div>
              <div className="font-body text-[10px] text-ink/45">500 × 500 PNG</div>
            </div>
          </button>
          <button
            onClick={handleLinkedIn}
            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-[4px] hover:bg-[#0A66C2]/8 text-left transition-colors"
            data-testid={`district-share-linkedin-${district}`}
            data-linkedin-url={liUrl}
          >
            <Linkedin className="w-3.5 h-3.5 text-[#0A66C2]" strokeWidth={1.8} />
            <div className="flex-1 min-w-0">
              <div className="font-body text-[11.5px] font-medium text-ink">Share on LinkedIn</div>
              <div className="font-body text-[10px] text-ink/45">Also downloads PNG</div>
            </div>
          </button>
          <button
            onClick={handleCopy}
            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-[4px] hover:bg-ink/[0.04] text-left transition-colors"
            data-testid={`district-share-copy-${district}`}
          >
            {copied
              ? <Check className="w-3.5 h-3.5 text-status-green" strokeWidth={2.2} />
              : <Link2 className="w-3.5 h-3.5 text-ink/55" strokeWidth={1.8} />}
            <div className="flex-1 min-w-0">
              <div className="font-body text-[11.5px] font-medium text-ink">{copied ? 'Copied!' : 'Copy link + caption'}</div>
              <div className="font-body text-[10px] text-ink/45 truncate">/search?district={district}</div>
            </div>
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Inline variant — three flat buttons in a row, no popover ────────
export function DistrictShareInline({ district, name, invisiblePct }) {
  const [copied, setCopied] = useState(false);

  const { shareUrl, caption } = _buildShare({ district, name, invisiblePct });
  const { liUrl, downloadPng, openLinkedIn, copyLink } = useCardShare({
    target: district,
    cardPath: `postcode-card/${district}.png`,
    shareUrl,
    caption,
    filename: `stockton-${district.toLowerCase()}-reality.png`,
  });

  const handleCopy = async () => {
    const ok = await copyLink();
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`district-share-inline-${district}`}>
      <button
        onClick={downloadPng}
        title="Download 500×500 PNG card"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/15 bg-white/[0.06] hover:bg-white/[0.12] hover:border-brand-orange/40 transition-colors font-body text-[11px] text-white/85"
        data-testid={`district-share-inline-download-${district}`}
      >
        <Download className="w-3 h-3" strokeWidth={2} />
        Share {district} card
      </button>
      <button
        onClick={openLinkedIn}
        title="Share on LinkedIn (also downloads PNG)"
        className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-white/15 bg-white/[0.06] hover:bg-[#0A66C2]/20 hover:border-[#0A66C2]/50 transition-colors text-white/75 hover:text-white"
        data-testid={`district-share-inline-linkedin-${district}`}
        data-linkedin-url={liUrl}
      >
        <Linkedin className="w-3 h-3" strokeWidth={2} />
      </button>
      <button
        onClick={handleCopy}
        title="Copy link + caption (also downloads PNG)"
        className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-white/15 bg-white/[0.06] hover:bg-white/[0.12] hover:border-brand-orange/40 transition-colors text-white/75 hover:text-white"
        data-testid={`district-share-inline-copy-${district}`}
      >
        {copied
          ? <Check className="w-3 h-3 text-status-green" strokeWidth={2.5} />
          : <Link2 className="w-3 h-3" strokeWidth={2} />}
      </button>
    </div>
  );
}
